import {autoinject, transient, useView} from "aurelia-framework";
import {BusinessUnitModel, BusinessUnitVetAlertModel} from "../../../../../../models/customers/customer.models";
import { VettingActionEnum } from "../../../../../../models/vetting/vetting.models";
import { BindingSignaler } from "aurelia-templating-resources";
import { UserClaimsService } from "../../../../../../services/authentication/user-claims.service";
import { Claims } from "../../../../../../constants/authentication/claims";
import { EntityTypeConfigurationHelper, EntityTypeConfigurationModel } from "../../../../../../models/basics/basics.models";
import { EntityFinderSearch } from "../../../../../components/entity-finder/entity-finder-search";

@autoinject
@transient()
@useView('views/pages/modules/business-unit/tabs/business-unit-email-alert-setup/business-unit-email-alert-setup.html')
export class BusinessUnitEmailAlertSetup {

    model: BusinessUnitModel;
    filteredIds: number [] = [];
    canEditBusinessUnitEmailAlerts: boolean = false;
    private _isAttached: boolean = false;
    public portsEntityFinderVM: { [id: number]: EntityFinderSearch } = {};
    public portEntityTypes: EntityTypeConfigurationModel[];

    constructor( private userClaimsService: UserClaimsService,
        private bindingSignaler: BindingSignaler,
        private entityTypeConfigurationHelper: EntityTypeConfigurationHelper)
    {
        this.portEntityTypes = this.entityTypeConfigurationHelper.getConfigurationWithoutPermissions(["Ports"]);
        this.canEditBusinessUnitEmailAlerts = this.userClaimsService.hasPermission(Claims.CanEditBUEmailAlerts);
    }

    activate(model: BusinessUnitModel) {
        this.model = model;
        this.filteredIds = [];
        if(this._isAttached)
        {
            setTimeout(() => {
                this.setSelectedPorts();
            });
        }
    }
    
    attached(){
        this.filteredIds = [];
        this._isAttached = true;
        setTimeout(() => {
            this.setSelectedPorts();
        });
    }

    setSelectedPorts()
    {
        if(this.model.businessUnitVetAlerts && this.model.businessUnitVetAlerts.length > 0)
        {
            this.model.businessUnitVetAlerts.forEach(alert => {
                if(alert && alert.businessUnitVetAlertPorts && alert.businessUnitVetAlertPorts.length > 0 && this.portsEntityFinderVM && this.portsEntityFinderVM[alert.businessUnitVetAlertId])
                {
                    alert.businessUnitVetAlertPorts.forEach(port => {
                        this.portsEntityFinderVM[alert.businessUnitVetAlertId].set({entityTypeUri: "Ports", id: port.portId});
                    });
                }
            });
        }
    }

    vettingActionSelected(index: number){
        if(index > -1 && this.hideVetRequesterOption(this.model.businessUnitVetAlerts[index]))
        {
            this.model.businessUnitVetAlerts[index].businessUnitVetAlertRecipients.forEach(x => {
                x.forVetRequester = false;
            });
        }
        if(index > -1 && this.hideShipManagerOption(this.model.businessUnitVetAlerts[index]))
        {
            this.model.businessUnitVetAlerts[index].businessUnitVetAlertRecipients.forEach(x => {
                x.forShipManager = false;
            });
        }
        this.bindingSignaler.signal('vetting-action-selected-' + index)
    }

    getCheckboxValue(x: BusinessUnitVetAlertModel) {
        if(x.vettingActionId >= VettingActionEnum.GHGIsRequested && x.vettingActionId <= VettingActionEnum.GHGRequiresFurtherInformation)
        {
            return "GHG Requester"
        }
        else if (this.showCollaborators(x))
        {
            return "Requester"
        }

        return "Vet Requester";
    }

    onboardingConfigIds(x: BusinessUnitVetAlertModel){
        return x.businessUnitVetAlertOnboardingConfigs.map(x => x.onboardingConfigId)
    }

    rfiTemplateIds(x: BusinessUnitVetAlertModel) {
        return x.businessUnitVetAlertRfiTemplates.map(x => x.rfiTemplateId)
    }

    vesselTypeIds(x: BusinessUnitVetAlertModel) {
        return x.businessUnitVetAlertVesselTypes.map(x => x.vesselTypeId);
    }

    incoTermIds(x: BusinessUnitVetAlertModel) {
        return x.businessUnitVetAlertIncoTerms.map(x => x.incoTermId);
    }

    selectedPortIds(x: BusinessUnitVetAlertModel) {
        return x.businessUnitVetAlertPorts.map(x => x.portId);
    }

    onboardingConfigIdsChanged(buVetAlertModel: BusinessUnitVetAlertModel, e) {
        const selectedItems = e.detail.filter(x => x.isSelected);
        this.changeHandler(selectedItems, buVetAlertModel.businessUnitVetAlertOnboardingConfigs, 'onboardingConfigId')
    }

    rfiTemplateIdsChanged(buVetAlertModel: BusinessUnitVetAlertModel, e) {
        const selectedItems = e.detail.filter(x => x.isSelected);
        this.changeHandler(selectedItems, buVetAlertModel.businessUnitVetAlertRfiTemplates, 'rfiTemplateId')
    }

    vesselTypeIdsChanged(buVetAlertModel: BusinessUnitVetAlertModel, e) {
        const selectedItems = e.detail.filter(x => x.isSelected);
        this.changeHandler(selectedItems, buVetAlertModel.businessUnitVetAlertVesselTypes, 'vesselTypeId')
    }
    
    incoTermIdsChanged(buVetAlertModel: BusinessUnitVetAlertModel, e) {
        const selectedItems = e.detail.filter(x => x.isSelected);
        this.changeHandler(selectedItems, buVetAlertModel.businessUnitVetAlertIncoTerms, 'incoTermId')
    }

    portIdsChanged(buVetAlertModel: BusinessUnitVetAlertModel, e) {
        if(!e || !e.detail) return;
        const selectedItems = e.detail.map(x => x && x.value && x.value.id > 0? x.value: null).filter(x => x != null);
        this.changeHandler(selectedItems, buVetAlertModel.businessUnitVetAlertPorts, 'portId')
    }

    vettingActionIdsChanged($event){
        const selected = <[]>$event.detail.filter(x => x.isSelected);

        if(selected.length > 0){
            this.filteredIds = selected.map(x=> (<any>x).id);
        }
        else {
            //all selected
            this.filteredIds = [];
        }
    }

    addEmail(x: BusinessUnitVetAlertModel) {
        x.businessUnitVetAlertRecipients.push({
            email: "",
            emailRecipientTypeId: 0,
            forVetRequester: false,
            forShipManager: false,
            forVoyageDataSubmitter: false,
            forFbrSubmitter: false,
            forCollaborators: false
        });
    }

    deleteEmail(x: BusinessUnitVetAlertModel, index: number) {
        x.businessUnitVetAlertRecipients.splice(index, 1);
    }

    deleteAlert(index: number) {
        this.model.businessUnitVetAlerts.splice(index, 1);
    }

    addAlert() {
        const x = {
            businessUnitVetAlertOnboardingConfigs: [],
            businessUnitVetAlertIncoTerms: [],
            businessUnitVetAlertRecipients: [],
            businessUnitVetAlertVesselTypes: [],
            businessUnitVetAlertPorts: [],
            businessUnitVetAlertRfiTemplates: [],
            vettingActionId: 0,
            customerId: this.model.customerId,
            timeBeforeExpireInDays: null,
            isActive: true
        };
        this.model.businessUnitVetAlerts.push(x);

        setTimeout(() => {
            this.addEmail(x);
        });
    }

    allowSelectFormTypes(x: BusinessUnitVetAlertModel){
        // if TQ
        return x.vettingActionId == VettingActionEnum.TQAwaitingResponse
        || x.vettingActionId == VettingActionEnum.TQRequireReview
        || x.vettingActionId == VettingActionEnum.TQComplete
        || x.vettingActionId == VettingActionEnum.EmailFormRequested;
    }

    allowSelectRfiTemplateTypes(x: BusinessUnitVetAlertModel){
        return x.vettingActionId == VettingActionEnum.RequiredRFI ||
            x.vettingActionId == VettingActionEnum.WindwardVoiFails;
    }

    allowSelectVesselTypes(x: BusinessUnitVetAlertModel){
        // if not ghg, ca or fbr vetting action
        return !((x.vettingActionId >= VettingActionEnum.GHGIsRequested && x.vettingActionId <= VettingActionEnum.GHGRequiresFurtherInformation) || 
            x.vettingActionId == VettingActionEnum.CAReportingPeriodExpire || x.vettingActionId == VettingActionEnum.VoyageDataHasAStatusChange
            || x.vettingActionId == VettingActionEnum.FBRRequiresFurtherInformation || x.vettingActionId == VettingActionEnum.RequiredRFI || x.vettingActionId == VettingActionEnum.WindwardVoiFails);
    }

    allowSelectIncoTerms(x: BusinessUnitVetAlertModel){
        // if not ghg, ca or fbr vetting action
        return !((x.vettingActionId >= VettingActionEnum.GHGIsRequested && x.vettingActionId <= VettingActionEnum.GHGRequiresFurtherInformation) || 
            x.vettingActionId == VettingActionEnum.CAReportingPeriodExpire || x.vettingActionId == VettingActionEnum.VoyageDataHasAStatusChange
            || x.vettingActionId == VettingActionEnum.FBRRequiresFurtherInformation || x.vettingActionId == VettingActionEnum.RequiredRFI || x.vettingActionId == VettingActionEnum.WindwardVoiFails);
    }

    isFbrAlert(x: BusinessUnitVetAlertModel){
        // if FBR
        return x.vettingActionId == VettingActionEnum.FBRRequiresFurtherInformation;
    }

    showTimeBeforeExpire(x: BusinessUnitVetAlertModel) {
        // if CA reporting period expire action
        return x.vettingActionId == VettingActionEnum.CAReportingPeriodExpire;
    }

    showVoyageSubmitter(x: BusinessUnitVetAlertModel) {
        return x.vettingActionId == VettingActionEnum.VoyageDataHasAStatusChange;
    }

    showCollaborators(x: BusinessUnitVetAlertModel) {
        return x.vettingActionId == VettingActionEnum.DueDiligenceIsRequested 
            || x.vettingActionId == VettingActionEnum.DueDiligenceHasAnOutcome
            || x.vettingActionId == VettingActionEnum.DueDiligenceRequireIntervention
            || x.vettingActionId == VettingActionEnum.DueDiligenceHasStatusChange;
    }

    isCarbonAccountingAlert(x: BusinessUnitVetAlertModel) {
        // if action is Carbon Accounting action
        return x.vettingActionId == VettingActionEnum.CAReportingPeriodExpire || x.vettingActionId == VettingActionEnum.VoyageDataHasAStatusChange;
    }

    hideVetRequesterOption(x: BusinessUnitVetAlertModel) {
        return this.isFbrAlert(x) || this.isCarbonAccountingAlert(x)
            || x.vettingActionId == VettingActionEnum.VetRequiresFurtherInformation;
    }

    hideShipManagerOption(x: BusinessUnitVetAlertModel) {
        return this.isFbrAlert(x) || this.isCarbonAccountingAlert(x)
            || x.vettingActionId == VettingActionEnum.VetRequiresFurtherInformation;
    }

    private changeHandler(aList: any[], bList: any[], idPropertyName: string){
        
        //Add new items
        for (const aItem of aList){
            const existingItem = bList.filter(x => x[idPropertyName] == aItem.id)[0];
            if (!existingItem){
                const newObject = {};
                newObject[idPropertyName] = aItem.id;
                bList.push(newObject);
            }
        }

        //Remove old items
        for (let i = bList.length -1; i >= 0; i--){
            const bItem = bList[i];
            const existingItem = aList.filter(x => x.id == bItem[idPropertyName])[0];
            
            if (!existingItem){
                const index = bList.indexOf(bItem);
                if (index !== -1){
                    bList.splice(index, 1);
                }
            }
        }
    }
}